import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppConstant } from '../../../../../../../Assests/AppConstant';
import { sagaActions } from '../../../../../../../../sagas/sagaActions';
import {
    getReleaseBannerAdminSelector,
    setCloseReleaseBannerAdminForm,
} from '../../../../../../../../redux/reducers/reducerSlices/ReleaseBannerAdminReducer';
import { shouldHavePermission } from '../../../../../../../Assests/Utility';
import { getUserSelector } from '../../../../../../../../redux/reducers/reducerSlices/UserReducer';
import WarehouseSearch from '../../../../../../SuperWarehouse/Components/SearchWarehouse/WarehouseSearch';
import ReleaseBannerList from '../ReleaseBannerList/ReleaseBannerList';
import Popup from '../../../../../../Common/Popup/Popup';
import { CustomDynamicForm } from '../../../../../../Common/CustomForm/CustomDynamicForm';

const { releaseBannerAdmin } = AppConstant.superUserAdmin;

/** Available App Types */
const APP_TYPE_OPTIONS = ['Spro/IMS', 'EAC'];

/** Available Notification Types */
const NOTIFICATION_TYPE_OPTIONS = ['Release', 'Notification'];

/** Severity levels: label → stored integer value */
const SEVERITY_OPTIONS = [
    { label: 'Low',    value: 0 },
    { label: 'Medium', value: 1 },
    { label: 'High',   value: 2 },
];

/** Returns a date string formatted as YYYY-MM-DD */
const toDateStr = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Returns the current local datetime string in "YYYY-MM-DDTHH:mm" format,
 * suitable as the `min` value for a datetime-local input.
 */
const getNowLocalDatetimeStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Converts a datetime-local input value (browser local time, format "YYYY-MM-DDTHH:mm")
 * into a UTC datetime string "YYYY-MM-DD HH:MM:SS" for API storage.
 * The `datetime-local` input always yields local-browser-timezone values.
 * We pass them through `new Date()` which interprets them as local, then format as UTC.
 */
const localDatetimeToUtcStr = (localDatetimeStr) => {
    if (!localDatetimeStr) return null;
    // datetime-local yields "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
    const d = new Date(localDatetimeStr);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

/**
 * Converts a UTC datetime string "YYYY-MM-DD HH:MM:SS" (as stored by the API)
 * into a local datetime string "YYYY-MM-DDTHH:mm" suitable for pre-populating
 * a datetime-local input (which always displays in browser local time).
 */
const utcStrToLocalDatetimeInput = (utcStr) => {
    if (!utcStr) return '';
    // Append Z so JS Date parses it as UTC, then format in local time
    const d = new Date(utcStr.replace(' ', 'T') + (utcStr.includes('Z') || utcStr.includes('+') ? '' : 'Z'));
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const getTodayStr = () => toDateStr(new Date());

const getOneMonthAgoStr = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toDateStr(d);
};

const VERSION_PATTERN = /^[\d.]+$/;

const ReleaseBannerAdminScreen = (props) => {

    const dispatch = useDispatch();
    const { themeSelected } = props;
    const [searchStr, setSearchStr] = useState('');
    const user = useSelector(getUserSelector);
    const { permissions, cu_id } = user;
    const releaseBannerAdminState = useSelector(getReleaseBannerAdminSelector);
    const { closeReleaseBannerAdminForm } = releaseBannerAdminState;

    const [allowPermissionC, setAllowPermissionC] = useState(false);
    const [allowPermissionE, setAllowPermissionE] = useState(false);
    const [allowPermissionD, setAllowPermissionD] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [loading, setLoading] = useState(false);
    const [resetFormData, setResetFormData] = useState(0);

    /**
     * selectedNotificationType drives which fields are shown in the form.
     * It is updated via getFormFieldData when the user changes the Notification Type
     * select inside CustomDynamicForm.
     */
    const [selectedNotificationType, setSelectedNotificationType] = useState('Release');

    /** Controlled checkbox for forceUpdate — read at submit time. */
    const [forceUpdateChecked, setForceUpdateChecked] = useState(false);

    /**
     * 3-level severity for Notification banners.
     * 0 = Low (default), 1 = Medium, 2 = High.
     */
    const [severityLevel, setSeverityLevel] = useState(0);

    /**
     * Multi-select App Type state.
     * Stored as an array; sent to API as comma-separated string.
     */
    const [selectedAppTypes, setSelectedAppTypes] = useState([]);

    /**
     * Controlled startDateTime for Notification type (datetime-local string, local time).
     * Rendered outside CustomDynamicForm to allow full control over min/required.
     */
    const [formStartDateTime, setFormStartDateTime] = useState('');

    /**
     * Controlled endDateTime for Notification type (datetime-local string, local time).
     * Rendered outside CustomDynamicForm to allow full control over min/required.
     */
    const [formEndDateTime, setFormEndDateTime] = useState('');

    /** Validation errors for the out-of-form datetime fields */
    const [dateTimeErrors, setDateTimeErrors] = useState({ startDateTime: '', endDateTime: '' });

    // Filter state
    const [filterStartDate, setFilterStartDate] = useState(getOneMonthAgoStr);
    const [filterEndDate, setFilterEndDate] = useState(getTodayStr);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterAppType, setFilterAppType] = useState('all');
    const [filterNotificationType, setFilterNotificationType] = useState('all');

    useEffect(() => {
        if (permissions && permissions.length) {
            setAllowPermissionC(shouldHavePermission(permissions, '601'));
            setAllowPermissionE(shouldHavePermission(permissions, '602'));
            setAllowPermissionD(shouldHavePermission(permissions, '603'));
        }
    }, [permissions]);

    const fetchWithFilters = (overrides = {}) => {
        const startDate        = overrides.startDate        !== undefined ? overrides.startDate        : filterStartDate;
        const endDate          = overrides.endDate          !== undefined ? overrides.endDate          : filterEndDate;
        const status           = overrides.status           !== undefined ? overrides.status           : filterStatus;
        const appType          = overrides.appType          !== undefined ? overrides.appType          : filterAppType;
        const notificationType = overrides.notificationType !== undefined ? overrides.notificationType : filterNotificationType;
        const search           = overrides.search           !== undefined ? overrides.search           : searchStr;

        const params = {};
        if (appType && appType !== 'all')                   params.service          = appType;
        if (notificationType && notificationType !== 'all') params.notificationType = notificationType;
        if (status === 'active')                            params.isActive         = 1;
        if (status === 'inactive')                          params.isActive         = 0;
        if (startDate)                                      params.startDate        = startDate;
        if (endDate)                                        params.endDate          = endDate;
        if (search && search.length > 2)                    params.search           = search;

        dispatch({ type: sagaActions.FETCH_RELEASE_BANNERS_ADMIN, payload: params });
    };

    useEffect(() => { fetchWithFilters(); }, []);
    useEffect(() => { fetchWithFilters(); }, [filterStartDate, filterEndDate, filterStatus, filterAppType, filterNotificationType, searchStr]);

    useEffect(() => {
        if (closeReleaseBannerAdminForm) {
            setLoading(false);
            closePopup();
            dispatch(setCloseReleaseBannerAdminForm(false));
        }
    }, [closeReleaseBannerAdminForm]);

    const getNow = () => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const handleRefresh = () => {
        const startDate = getOneMonthAgoStr();
        const endDate = getTodayStr();
        setSearchStr('');
        setFilterStartDate(startDate);
        setFilterEndDate(endDate);
        setFilterStatus('all');
        setFilterAppType('all');
        setFilterNotificationType('all');
        dispatch({ type: sagaActions.FETCH_RELEASE_BANNERS_ADMIN, payload: { startDate, endDate } });
    };

    const closePopup = () => {
        setEditData({});
        setForceUpdateChecked(false);
        setSeverityLevel(0);
        setSelectedAppTypes([]);
        setSelectedNotificationType('Release');
        setFormStartDateTime('');
        setFormEndDateTime('');
        setDateTimeErrors({ startDateTime: '', endDateTime: '' });
        setShowModal(false);
        setResetFormData(prev => prev + 1);
    };

    const openCreateForm = () => {
        setEditData({});
        setForceUpdateChecked(false);
        setSeverityLevel(0);
        setSelectedAppTypes([]);
        setSelectedNotificationType('Release');
        setFormStartDateTime('');
        setFormEndDateTime('');
        setDateTimeErrors({ startDateTime: '', endDateTime: '' });
        setShowModal(true);
    };

    const editBannerAction = (data) => {
        const fu = data.forceUpdate === 1 || data.forceUpdate === true;
        // severity stored as 0/1/2 integer; default to 0 (Low) if falsy
        const sv = typeof data.severity === 'number' ? data.severity : (data.severity ? 1 : 0);
        const notifType = data.notificationType || 'Release';
        // App type stored as comma-separated string; split into array for multi-select
        const appTypesArr = data.service
            ? data.service.split(',').map(s => s.trim()).filter(Boolean)
            : [];
        // Convert UTC strings from API → local datetime-local input format
        const startLocal = utcStrToLocalDatetimeInput(data.startDateTime);
        const endLocal   = utcStrToLocalDatetimeInput(data.endDateTime);
        setForceUpdateChecked(fu);
        setSeverityLevel(sv);
        setSelectedAppTypes(appTypesArr);
        setSelectedNotificationType(notifType);
        // Populate controlled datetime fields for Notification type
        setFormStartDateTime(notifType === 'Notification' ? (startLocal || '') : '');
        setFormEndDateTime(notifType === 'Notification' ? (endLocal || '') : '');
        setDateTimeErrors({ startDateTime: '', endDateTime: '' });
        setEditData({
            id: data.id,
            notificationType: notifType,
            prevVersion: data.prevVersion,
            currentVersion: data.currentVersion,
            bannerHeading: data.bannerHeading,
            bannerText: data.bannerText,
            bannerSubtext: data.bannerSubtext,
            bannerLogo: data.bannerLogo,
            bannerDisclaimer: data.bannerDisclaimer,
            forceUpdate: fu,
            severity: sv,
            // Convert UTC strings from API → local datetime-local input format
            // (datetime-local always displays in browser local time)
            releaseDate: utcStrToLocalDatetimeInput(data.releaseDate),
            isActive: data.isActive,
            isDeleted: data.isDeleted,
        });
        setShowModal(true);
    };

    const validateVersion = (value, fieldName) => {
        if (!value) return null;
        if (!VERSION_PATTERN.test(value)) {
            return `${fieldName} must contain only numbers and dots (e.g. 1.0.0)`;
        }
        return null;
    };

    /**
     * Toggles an app type in/out of the selectedAppTypes array.
     */
    const toggleAppType = (appType) => {
        setSelectedAppTypes(prev =>
            prev.includes(appType)
                ? prev.filter(t => t !== appType)
                : [...prev, appType]
        );
    };

    /**
     * Called by CustomDynamicForm whenever any field changes.
     * Used to detect Notification Type changes so we can update selectedNotificationType
     * and cause the form to remount with the correct conditional fields.
     */
    const handleFormFieldData = (data, name) => {
        if (name === 'notificationType') {
            setForceUpdateChecked(false);
            setSeverityLevel(0);
            setFormStartDateTime('');
            setFormEndDateTime('');
            setDateTimeErrors({ startDateTime: '', endDateTime: '' });
            // Do NOT reset selectedAppTypes — preserve App Type selections across type switch
            setSelectedNotificationType(data);
            // Also sync editData so the remounted form (key changes → full remount) pre-populates
            // the notificationType select with the newly chosen value instead of showing blank.
            setEditData(prev => ({ ...prev, notificationType: data }));
        }
    };

    const getSummitedFormData = (data) => {
        if (!data) return;

        const notifType = selectedNotificationType;

        // App type — must have at least one selected
        if (selectedAppTypes.length === 0) {
            alert('Please select at least one App Type.');
            return;
        }

        if (notifType === 'Release') {
            const prevErr = validateVersion(data.prevVersion, 'Previous Version');
            const currErr = validateVersion(data.currentVersion, 'Current Version');
            if (prevErr || currErr) {
                alert(prevErr || currErr);
                return;
            }
        }

        // For Notification type — validate start and end datetime are provided
        if (notifType === 'Notification') {
            const errs = { startDateTime: '', endDateTime: '' };
            if (!formStartDateTime) errs.startDateTime = 'Start Date & Time is required.';
            if (!formEndDateTime)   errs.endDateTime   = 'End Date & Time is required.';
            if (errs.startDateTime || errs.endDateTime) {
                setDateTimeErrors(errs);
                return;
            }
            // Also validate end is after start
            if (formStartDateTime && formEndDateTime && formEndDateTime <= formStartDateTime) {
                setDateTimeErrors(prev => ({ ...prev, endDateTime: 'End Date & Time must be after Start Date & Time.' }));
                return;
            }
        }

        setLoading(true);
        const now = getNow();
        const forceUpdateInt = forceUpdateChecked ? 1 : 0;
        // severityLevel is already 0/1/2
        const severityInt = severityLevel;

        const commonFields = {
            notificationType: notifType,
            // Send as comma-separated string
            service: selectedAppTypes.join(','),
            bannerHeading: data.bannerHeading,
            bannerText: data.bannerText,
            bannerSubtext: data.bannerSubtext,
            bannerLogo: data.bannerLogo,
            bannerDisclaimer: data.bannerDisclaimer,
        };

        if (data.id) {
            let payload = {
                id: data.id,
                ...commonFields,
                isActive: editData.isActive ? 1 : 0,
                isDeleted: editData.isDeleted ? 1 : 0,
                updatedBy: cu_id,
                updatedOn: now,
            };
            if (notifType === 'Release') {
                // releaseDate comes from datetime-local (local browser time) → convert to UTC "YYYY-MM-DD HH:MM:SS"
                const releaseDateUtc = localDatetimeToUtcStr(data.releaseDate) || now;
                payload = { ...payload, prevVersion: data.prevVersion, currentVersion: data.currentVersion, forceUpdate: forceUpdateInt, releaseDate: releaseDateUtc, isReleaseDone: 0 };
            } else {
                // Use controlled state values (rendered outside CustomDynamicForm) → convert local to UTC
                const startUtc = localDatetimeToUtcStr(formStartDateTime) || now;
                const endUtc   = localDatetimeToUtcStr(formEndDateTime)   || now;
                payload = { ...payload, severity: severityInt, startDateTime: startUtc, endDateTime: endUtc };
            }
            dispatch({ type: sagaActions.UPDATE_RELEASE_BANNER_ADMIN, payload });
        } else {
            let payload = { ...commonFields, isActive: 1, isDeleted: 0, createdBy: cu_id, createdOn: now };
            if (notifType === 'Release') {
                const releaseDateUtc = localDatetimeToUtcStr(data.releaseDate) || now;
                payload = { ...payload, prevVersion: data.prevVersion, currentVersion: data.currentVersion, forceUpdate: forceUpdateInt, releaseDate: releaseDateUtc, isReleaseDone: 0 };
            } else {
                // Use controlled state values (rendered outside CustomDynamicForm) → convert local to UTC
                const startUtc = localDatetimeToUtcStr(formStartDateTime) || now;
                const endUtc   = localDatetimeToUtcStr(formEndDateTime)   || now;
                payload = { ...payload, severity: severityInt, startDateTime: startUtc, endDateTime: endUtc };
            }
            dispatch({ type: sagaActions.CREATE_RELEASE_BANNER_ADMIN, payload });
        }
    };

    const changeActiveStatus = (data) => {
        const now = getNow();
        dispatch({
            type: sagaActions.UPDATE_RELEASE_BANNER_ADMIN,
            payload: {
                id: data.id, notificationType: data.notificationType, service: data.service,
                prevVersion: data.prevVersion, currentVersion: data.currentVersion,
                releaseDate: data.releaseDate, startDateTime: data.startDateTime, endDateTime: data.endDateTime,
                bannerHeading: data.bannerHeading, bannerText: data.bannerText, bannerSubtext: data.bannerSubtext,
                bannerLogo: data.bannerLogo, bannerDisclaimer: data.bannerDisclaimer,
                forceUpdate: data.forceUpdate, severity: data.severity,
                isReleaseDone: 0, isActive: data.isActive ? 0 : 1, isDeleted: data.isDeleted,
                updatedBy: cu_id, updatedOn: now,
            },
        });
    };

    const changeDeletedStatus = (data) => {
        const now = getNow();
        dispatch({ type: sagaActions.DELETE_RELEASE_BANNER_ADMIN, payload: { id: data.id, updatedBy: cu_id, updatedOn: now } });
    };

    const getNotificationTypeOptions = () =>
        NOTIFICATION_TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>{type}</option>
        ));

    /**
     * Builds the form field list.
     * notificationType is now rendered as a standalone select OUTSIDE CustomDynamicForm
     * (so Severity can appear immediately below it, before the remaining fields).
     * CustomDynamicForm receives only banner content fields + type-specific fields.
     */
    const buildFormFieldList = () => {
        // App Type is handled outside CustomDynamicForm as multi-select checkboxes.
        // notificationType is handled outside CustomDynamicForm as a standalone select.
        // Severity is handled outside CustomDynamicForm as radio buttons.
        // Only banner content fields + type-specific fields go into CustomDynamicForm.
        const commonFields = [
            {
                name: 'bannerHeading',
                type: 'text',
                size: 'col-sm-6',
                position: 4,
                title: releaseBannerAdmin.formFields.bannerHeading,
                formValidation: { required: true },
            },
            {
                name: 'bannerText',
                type: 'textarea',
                size: 'col-sm-6',
                position: 5,
                title: releaseBannerAdmin.formFields.bannerText,
                formValidation: { required: false },
            },
            {
                name: 'bannerSubtext',
                type: 'textarea',
                size: 'col-sm-6',
                position: 6,
                title: releaseBannerAdmin.formFields.bannerSubtext,
                formValidation: { required: false },
            },
            // Banner Logo URL — commented out; uncomment when needed
            // {
            //     name: 'bannerLogo',
            //     type: 'text',
            //     size: 'col-sm-6',
            //     position: 7,
            //     title: releaseBannerAdmin.formFields.bannerLogo,
            //     formValidation: { required: false },
            // },
            {
                name: 'bannerDisclaimer',
                type: 'textarea',
                size: 'col-sm-6',
                position: 8,
                title: releaseBannerAdmin.formFields.bannerDisclaimer,
                formValidation: { required: false },
            },
        ];

        if (selectedNotificationType === 'Release') {
            return [
                ...commonFields,
                {
                    name: 'prevVersion',
                    type: 'text',
                    size: 'col-sm-6',
                    position: 3,
                    title: releaseBannerAdmin.formFields.prevVersion,
                    placeholder: 'e.g. 1.0.0',
                    formValidation: {
                        required: false,
                        pattern: { value: /^[\d.]*$/, message: 'Only numbers and dots are allowed (e.g. 1.0.0)' },
                    },
                },
                {
                    name: 'currentVersion',
                    type: 'text',
                    size: 'col-sm-6',
                    position: 3,
                    title: releaseBannerAdmin.formFields.currentVersion,
                    placeholder: 'e.g. 1.1.0',
                    formValidation: {
                        required: true,
                        pattern: { value: /^[\d.]+$/, message: 'Only numbers and dots are allowed (e.g. 1.1.0)' },
                    },
                },
                {
                    name: 'forceUpdate',
                    type: 'checkbox',
                    size: 'col-sm-6 column-select-checkbox mt-3',
                    position: 9,
                    title: releaseBannerAdmin.formFields.forceUpdate,
                    formValidation: { required: false },
                    checked: forceUpdateChecked,
                    onChange: (e) => setForceUpdateChecked(e.target.checked),
                },
                {
                    name: 'releaseDate',
                    type: 'datetime-local',
                    size: 'col-sm-6',
                    position: 10,
                    title: releaseBannerAdmin.formFields.releaseDate,
                    formValidation: { required: false },
                },
            ];
        }

        // Notification type — startDateTime and endDateTime are rendered as controlled
        // inputs OUTSIDE CustomDynamicForm (see createForm) so that we can enforce
        // min (no past dates) and required validation properly.
        return [...commonFields];
    };

    const submitbtnData = {
        show: true,
        text: AppConstant.superUserAdmin.warehouseAdmin.createWarehouseForm.saveButtonTitle,
    };

    /**
     * Notification Type is rendered as a standalone select OUTSIDE CustomDynamicForm.
     * Order: App Type → Notification Type → Severity (Notification only) → remaining fields (CustomDynamicForm).
     * key={selectedNotificationType} forces CustomDynamicForm to fully remount when type changes.
     */
    const createForm = () => (
        <div>
            {/* App Type — multi-select checkboxes (rendered outside CustomDynamicForm) */}
            <div className='container-fluid mb-2'>
                <div className='row'>
                    <div className='col-sm-12'>
                        <label className='fw-semibold small mb-1'>
                            {releaseBannerAdmin.formFields.service}
                            <span className='text-danger ms-1'>*</span>
                        </label>
                        <div className='d-flex flex-wrap gap-3'>
                            {APP_TYPE_OPTIONS.map((appType) => (
                                <div key={appType} className='form-check'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        id={`appType-${appType}`}
                                        checked={selectedAppTypes.includes(appType)}
                                        onChange={() => toggleAppType(appType)}
                                    />
                                    <label className='form-check-label' htmlFor={`appType-${appType}`}>
                                        {appType}
                                    </label>
                                </div>
                            ))}
                        </div>
                        {selectedAppTypes.length === 0 && (
                            <div className='text-danger small mt-1'>Please select at least one App Type.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Notification Type — standalone select rendered OUTSIDE CustomDynamicForm
                so Severity can appear immediately below it, before the remaining fields. */}
            <div className='container-fluid mb-2'>
                <div className='row'>
                    <div className='col-sm-6'>
                        <label className='fw-semibold small mb-1'>
                            {releaseBannerAdmin.formFields.notificationType}
                            <span className='text-danger ms-1'>*</span>
                        </label>
                        <select
                            className='form-select form-select-sm'
                            value={selectedNotificationType}
                            onChange={(e) => handleFormFieldData(e.target.value, 'notificationType')}
                        >
                            {NOTIFICATION_TYPE_OPTIONS.map((type) => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Severity — 3-level radio buttons, shown only for Notification type.
                Rendered immediately below Notification Type, above remaining fields. */}
            {selectedNotificationType === 'Notification' && (
                <div className='container-fluid mb-2'>
                    <div className='row'>
                        <div className='col-sm-12'>
                            <label className='fw-semibold small mb-1'>
                                {releaseBannerAdmin.formFields.severity}
                            </label>
                            <div className='d-flex flex-wrap gap-4'>
                                {SEVERITY_OPTIONS.map((opt) => (
                                    <div key={opt.value} className='form-check'>
                                        <input
                                            className='form-check-input'
                                            type='radio'
                                            name='severityLevel'
                                            id={`severity-${opt.value}`}
                                            value={opt.value}
                                            checked={severityLevel === opt.value}
                                            onChange={() => setSeverityLevel(opt.value)}
                                        />
                                        <label className='form-check-label' htmlFor={`severity-${opt.value}`}>
                                            {opt.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Start Date & Time / End Date & Time — controlled inputs for Notification type.
                Rendered outside CustomDynamicForm to enforce:
                  1. min = current local datetime (no past dates allowed)
                  2. required = true (mandatory for Notification type)
                  3. End min = selected start datetime (end must be ≥ start)               */}
            {selectedNotificationType === 'Notification' && (
                <div className='container-fluid mb-2'>
                    <div className='row g-2'>
                        <div className='col-sm-6'>
                            <label className='fw-semibold small mb-1' htmlFor='notif-startDateTime'>
                                {releaseBannerAdmin.formFields.startDateTime}
                                <span className='text-danger ms-1'>*</span>
                            </label>
                            <input
                                id='notif-startDateTime'
                                type='datetime-local'
                                className={`form-control form-control-sm${dateTimeErrors.startDateTime ? ' is-invalid' : ''}`}
                                value={formStartDateTime}
                                min={getNowLocalDatetimeStr()}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormStartDateTime(val);
                                    // Clear start error; also clear end error if end is now after new start
                                    setDateTimeErrors(prev => ({
                                        ...prev,
                                        startDateTime: val ? '' : prev.startDateTime,
                                        endDateTime: (formEndDateTime && val && formEndDateTime > val) ? '' : prev.endDateTime,
                                    }));
                                    // If current end is before new start, reset end
                                    if (formEndDateTime && val && formEndDateTime <= val) {
                                        setFormEndDateTime('');
                                    }
                                }}
                            />
                            {dateTimeErrors.startDateTime && (
                                <div className='text-danger small mt-1'>{dateTimeErrors.startDateTime}</div>
                            )}
                        </div>
                        <div className='col-sm-6'>
                            <label className='fw-semibold small mb-1' htmlFor='notif-endDateTime'>
                                {releaseBannerAdmin.formFields.endDateTime}
                                <span className='text-danger ms-1'>*</span>
                            </label>
                            <input
                                id='notif-endDateTime'
                                type='datetime-local'
                                className={`form-control form-control-sm${dateTimeErrors.endDateTime ? ' is-invalid' : ''}`}
                                value={formEndDateTime}
                                min={formStartDateTime || getNowLocalDatetimeStr()}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFormEndDateTime(val);
                                    setDateTimeErrors(prev => ({ ...prev, endDateTime: val ? '' : prev.endDateTime }));
                                }}
                            />
                            {dateTimeErrors.endDateTime && (
                                <div className='text-danger small mt-1'>{dateTimeErrors.endDateTime}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <CustomDynamicForm
                key={selectedNotificationType}
                formFieldList={buildFormFieldList()}
                sort={true}
                cancelBtn={null}
                submitbtn={submitbtnData}
                themeSelected={themeSelected}
                getSummitedFormData={getSummitedFormData}
                getFormFieldData={handleFormFieldData}
                editFormData={editData}
                loading={loading}
                resetFormData={resetFormData}
                setParticularFieldValue={editData}
            />
        </div>
    );

    const showBannerPopup = () => {
        const popupObj = {
            id: 'createReleaseBannerFormPopup',
            modalLabel: 'createReleaseBannerFormPopup',
            showClose: true,
            showfooter: false,
            showHeader: true,
            sendDriverNotification: false,
            modalSize: 'xl',
            title: Object.keys(editData).length
                ? releaseBannerAdmin.editBannerTitle
                : releaseBannerAdmin.createBannerTitle,
            bodyText: createForm(),
            subText: '',
            keyboard: false,
            backdrop: 'static',
            footerBtn: {
                btn1: { show: true, text: AppConstant.comments.cancelCommentButtonTitle },
                btn2: { show: true, text: AppConstant.superUserAdmin.warehouseAdmin.createWarehouseForm.saveButtonTitle },
            },
        };
        return (
            <Popup
                {...popupObj}
                popupBtnHandler={() => () => ''}
                closepopup={closePopup}
                themeSelected={themeSelected}
                showModal={showModal}
            />
        );
    };

    return (
        <div>
            {/* Top action bar */}
            <div className='container-fluid d-flex flex-wrap gap-2 py-2 align-items-center justify-content-between mt-1'>

                {/* Left: Create + Refresh */}
                <div className='d-flex gap-2 align-items-center'>
                    <button
                        className={allowPermissionC ? 'addButton my-1' : 'addButton my-1 disabled'}
                        onClick={openCreateForm}
                        disabled={!allowPermissionC}
                    >
                        {releaseBannerAdmin.createBannerTitle}
                    </button>
                    <button className='addButton my-1' onClick={handleRefresh}>
                        Refresh
                    </button>
                </div>

                {/* Center: Filters */}
                <div className='d-flex flex-wrap gap-2 align-items-center'>
                    <div className='d-flex align-items-center gap-1'>
                        <label className='fw-semibold small mb-0' style={{ whiteSpace: 'nowrap' }}>Start Date</label>
                        <input
                            type='date'
                            className='form-control form-control-sm'
                            style={{ maxWidth: 145 }}
                            value={filterStartDate}
                            onChange={e => setFilterStartDate(e.target.value)}
                        />
                    </div>
                    <div className='d-flex align-items-center gap-1'>
                        <label className='fw-semibold small mb-0' style={{ whiteSpace: 'nowrap' }}>End Date</label>
                        <input
                            type='date'
                            className='form-control form-control-sm'
                            style={{ maxWidth: 145 }}
                            value={filterEndDate}
                            onChange={e => setFilterEndDate(e.target.value)}
                        />
                    </div>
                    <div className='d-flex align-items-center gap-1'>
                        <label className='fw-semibold small mb-0'>Status</label>
                        <select
                            className='form-select form-select-sm'
                            style={{ maxWidth: 120 }}
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                        >
                            <option value='all'>All</option>
                            <option value='active'>Active</option>
                            <option value='inactive'>Inactive</option>
                        </select>
                    </div>
                    <div className='d-flex align-items-center gap-1'>
                        <label className='fw-semibold small mb-0' style={{ whiteSpace: 'nowrap' }}>Notification Type</label>
                        <select
                            className='form-select form-select-sm'
                            style={{ maxWidth: 140 }}
                            value={filterNotificationType}
                            onChange={e => setFilterNotificationType(e.target.value)}
                        >
                            <option value='all'>All</option>
                            {NOTIFICATION_TYPE_OPTIONS.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div className='d-flex align-items-center gap-1'>
                        <label className='fw-semibold small mb-0' style={{ whiteSpace: 'nowrap' }}>App Type</label>
                        <select
                            className='form-select form-select-sm'
                            style={{ maxWidth: 130 }}
                            value={filterAppType}
                            onChange={e => setFilterAppType(e.target.value)}
                        >
                            <option value='all'>All</option>
                            {APP_TYPE_OPTIONS.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        className='btn btn-sm btn-outline-secondary'
                        onClick={() => {
                            setFilterStartDate(getOneMonthAgoStr());
                            setFilterEndDate(getTodayStr());
                            setFilterStatus('all');
                            setFilterAppType('all');
                            setFilterNotificationType('all');
                        }}
                    >
                        Clear Filters
                    </button>
                </div>

                {/* Right: Search */}
                <div style={{ minWidth: '200px', maxWidth: '240px' }}>
                    <WarehouseSearch
                        placeholder={'Search banners...'}
                        inputValue={searchStr}
                        cancelSearch={() => setSearchStr('')}
                        onChangeHandler={(text) => setSearchStr(text)}
                    />
                </div>
            </div>

            <div className='d-flex justify-content-center'>
                <ReleaseBannerList
                    themeSelected={themeSelected}
                    allowPermissionE={allowPermissionE}
                    allowPermissionD={true}
                    editBannerAction={editBannerAction}
                    changeActiveStatus={changeActiveStatus}
                    changeDeletedStatus={changeDeletedStatus}
                />
            </div>
            {showBannerPopup()}
        </div>
    );
};

export default ReleaseBannerAdminScreen;
